import type { Ref } from 'vue'
import { onBeforeUnmount, ref, shallowRef, watch } from 'vue'

import type { PickedTimeSlots } from '@/components/organisms/TimeSlotPickerModal/useTimeSlot'
import { empty } from '@/components/organisms/TimeSlotPickerModal/useTimeSlot'
import { useUpdateLatestReadMessage } from '@/hooks/useUpdateLatestReadMessage'
import { container, TYPES } from '@/libs/di/inversify.config'
import { isClientError } from '@/libs/utilities/CustomErrors'
import { isDefinedRef } from '@/libs/utilities/isDefinedRef'
import type { SealedWebSocket } from '@/libs/utilities/sealedWebSocket'
import { wait } from '@/libs/utilities/wait'
import type { AnyMessage, AnyRealtimeMessage, Match, RoomID } from '@/types/app'
import { ChatMessageAttachmentType } from '@/types/schema.generated'
import { captureException } from '@sentry/vue'
import { useIntervalFn } from '@vueuse/core'
import {
  useSendNormalChatMessageMutation,
  useSendQuestionChatMessageMutation,
  useRequestReviewMutation,
} from './useMessageSocket.generated'
import { useLogger } from '@/logger/logger'

type RealtimeMessageHandler = (message: AnyRealtimeMessage) => Promise<void>

type ReviewRequestability = {
  isRequestable: boolean
  updateHook: 'addMessage' | 'never'
}

type ArgType = {
  matchRef: Ref<Maybe<Match>>
  reviewRequestabilityRef: Ref<ReviewRequestability>
  isDisabledToScrollToBottomRef: Ref<boolean>
  appendMessageHandler: (message: AnyMessage) => void
  onReconnected: () => Promise<void>
  fallbackRefreshHandler: () => Promise<void>
  entranceGUID: string
}

export const useMessageSocket = ({
  matchRef,
  reviewRequestabilityRef,
  isDisabledToScrollToBottomRef,
  appendMessageHandler,
  onReconnected,
  fallbackRefreshHandler,
  entranceGUID,
}: ArgType) => {
  const isUserTyping = ref(false)
  const isConnecting = ref(false)
  const isRequestingReview = ref(false)
  const messageWebSocket = shallowRef<Maybe<SealedWebSocket>>()

  const logger = useLogger()

  const { mutate: requestReview } = useRequestReviewMutation()
  const handleRequestReview = async () => {
    if (
      isRequestingReview.value ||
      !isDefinedRef<Match>(matchRef) ||
      !reviewRequestabilityRef.value.isRequestable
    ) {
      return
    }
    isRequestingReview.value = true
    await requestReview({ roomId: matchRef.value.roomId.toString() }).finally(
      () => {
        isRequestingReview.value = false
      }
    )
  }

  const { updateLatestReadMessage } = useUpdateLatestReadMessage()

  const connect = async (match: Match): Promise<void> => {
    if (isConnecting.value) {
      return
    }

    try {
      isConnecting.value = true
      closeMessageWebSocket()

      const handleRealtimeMessage = createRealtimeMessageHandler(
        match,
        isUserTyping,
        appendMessageHandler,
        updateLatestReadMessage,
        entranceGUID
      )

      await new Promise<void>((resolve, reject) => {
        messageWebSocket.value = container
          .get(TYPES.MessageUsecase)
          .createMessageWebsocket(
            match.roomId,
            entranceGUID,
            () => {
              resolve()
            },
            async message => {
              try {
                await handleRealtimeMessage(message)
              } catch (error) {
                // FIXME: ちゃんとハンドリングしてあげる

                if (!isClientError(error)) {
                  captureException(error)
                }
              }
            },
            event => {
              reject(event)
            },
            event => {
              reject(event)
            }
          )
      })
    } finally {
      isConnecting.value = false
    }
  }

  const reconnect = async (): Promise<void> => {
    if ((messageWebSocket.value?.isOpened ?? false) || matchRef.value == null) {
      return
    }

    try {
      await connect(matchRef.value)

      if (messageWebSocket.value?.isOpened) {
        await onReconnected()
      }
    } catch (e) {
      // websocketが接続できなくてもretry機構がある & REST経由でサービスの利用を継続できるので、logだけにしておく
      console.error(e)
    }
  }

  const RECONNECTION_INTERVAL = 1000 * 5 // 5 sec
  useIntervalFn(reconnect, RECONNECTION_INTERVAL)

  const handleTyping = async (isTyping: boolean) => {
    if (!isDefinedRef<SealedWebSocket>(messageWebSocket)) {
      return
    }

    container
      .get(TYPES.MessageUsecase)
      .sendTypingState(messageWebSocket.value as SealedWebSocket, isTyping)
  }

  const sendNormalChatMessageMutation = useSendNormalChatMessageMutation({})
  const sendQuestionChatMessageMutation = useSendQuestionChatMessageMutation({})

  const handleSendMessage = async (
    message: string,
    selections: string[],
    sendAsStaff: boolean,
    timeSlots: PickedTimeSlots = empty(),
    fallbackUrlForBookingRequest: Maybe<string> = undefined
  ) => {
    if (
      !isDefinedRef<Match>(matchRef) ||
      !isDefinedRef<SealedWebSocket>(messageWebSocket) ||
      message === ''
    ) {
      return
    }

    isDisabledToScrollToBottomRef.value = false

    if (selections.length > 0) {
      logger.ltlPeople.event.sendChatSelectionMessage(logger.buildMeta(), {
        data: {
          type: 'text',
          roomId: matchRef.value.roomId,
          message,
          selections: selections.map(value => ({
            content: value,
          })),
        },
      })

      if (messageWebSocket.value.isOpened) {
        container
          .get(TYPES.MessageUsecase)
          .sendTypingState(messageWebSocket.value, false)
      }

      await sendQuestionChatMessageMutation.mutate({
        input: {
          chatRoomId: matchRef.value.roomId.toString(),
          message,
          answerSelections: selections,
          sendAsStaff,
        },
      })
    } else {
      logger.ltlPeople.event.sendChatMessage(logger.buildMeta(), {
        data: {
          message,
          roomId: matchRef.value.roomId,
        },
      })

      if (messageWebSocket.value.isOpened) {
        container
          .get(TYPES.MessageUsecase)
          .sendTypingState(messageWebSocket.value, false)
      }

      await sendNormalChatMessageMutation.mutate({
        input: {
          chatRoomId: matchRef.value.roomId.toString(),
          message,
          sendAsStaff,
          attachments:
            timeSlots.length === 0
              ? []
              : [
                  {
                    type: ChatMessageAttachmentType.BookingRequest,
                    rawContent: JSON.stringify({
                      fallback_url: fallbackUrlForBookingRequest,
                      time_slots: timeSlots.map(ts => ({
                        starts_at: ts.startsAt,
                        ends_at: ts.endsAt,
                      })),
                    }),
                  },
                ],
        },
      })
    }

    if (!messageWebSocket.value.isOpened) {
      // レプリカラグがあるので、ちょっと待つ
      await wait(500)
      fallbackRefreshHandler()
    }
  }

  const closeMessageWebSocket = (): void => {
    messageWebSocket.value?.close()
  }

  onBeforeUnmount(closeMessageWebSocket)

  // match watcher
  watch(matchRef, async newMatch => {
    isUserTyping.value = false
    closeMessageWebSocket()

    if (newMatch === undefined) {
      return
    }

    try {
      await connect(newMatch)
    } catch (e) {
      // websocketが接続できなくてもretry機構がある & REST経由でサービスの利用を継続できるので、logだけにしておく
      console.log(e)
    }
  })

  return {
    closeMessageWebSocket,
    handleTyping,
    handleRequestReview,
    handleSendMessage,
    isUserTyping,
    isRequestingReview,
  }
}

const createRealtimeMessageHandler =
  (
    match: Match,
    isUserTypingRef: Ref<boolean>,
    appendMessageHandler: (message: AnyMessage) => void,
    updateLatestReadMessage: (
      roomId: RoomID,
      messageGUID: string,
      entranceGUID: string
    ) => void,
    entranceGUID: string
  ): RealtimeMessageHandler =>
  async (message: AnyRealtimeMessage): Promise<void> => {
    switch (message.type) {
      case 'deleted':
      case 'typing': {
        // match.planner.chatParticipantId は own かつ chatMessageList のときだけ存在する
        if (message.chatParticipantId !== match.planner.chatParticipantId) {
          isUserTypingRef.value = message.type === 'typing'
        }
        return
      }
      case 'normal':
      case 'livechat':
      case 'selections':
      case 'answer':
      case 'review': {
        appendMessageHandler(message)
        updateLatestReadMessage(match.roomId, message.guid, entranceGUID)
      }
    }
  }
